import { Theme } from "@mui/material";
import { Palette, SxProps } from "@mui/material/styles";
import { CSSProperties } from "@mui/material/styles/createTypography";
import { ComponentProps } from "react";
import { black, blue, boxShadows, grey, white } from "./color";
import {
	ACTIVE,
	ACTIVE_NOT_SELECTED,
	DISABLED,
	FOCUS,
	FOCUSED,
	FOCUS_VISIBLE,
	HOVER,
	HOVER_NOT_SELECTED,
	PLACEHOLDER,
	SELECT,
	SELECTED,
	TEXTFIELD_INNER_OUTLINE,
} from "designSystem/theme";

// common CSS classes and styles
export const CSS_ANCHOR_RESET = "csi-anchor-reset";
export const ICON_MEDIUM_SIZE = 26;
export const ICON_ONLY_SIZE = 16;
export const ICON_SMALL_SIZE = 20;
export const STANDARD_BORDER_STYLE = `1px solid ${grey[400]}`;
export const NO_WRAP: CSSProperties = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
};

// ellipsis text at a certain number of lines
export const TEXT_CLAMP = (clampLines: number): CSSProperties => {
	return {
		display: "-webkit-box",
		overflow: "hidden",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: clampLines,
	};
};

const childIconCurrentColor = {
	"& .MuiIcon-root": {
		color: "currentColor",
	},
	"& .MuiSvgIcon-root": {
		color: "currentColor",
	},
};

export const primaryBoxShadow = (palette: Palette): string => `0 0 1px 2px ${palette.FocusPrimary}`;

/**
 * Styling for item with hover styling (not clickable though)
 */
export function bannerStyle({ palette }: Theme): CSSProperties {
	const { LinkDefault, LinkHover } = palette;

	return {
		backgroundColor: blue[30],
		borderColor: blue[50],
		color: LinkDefault,

		[HOVER]: {
			color: LinkHover,
		},
	};
}

export function textFieldInnerOutlineStyle(theme?: Theme): CSSProperties {
	return {
		border: TEXTFIELD_INNER_OUTLINE,
	};
}

/**
 * Styling for item with hover styling (not clickable though)
 */
export function hoverStyle({ palette }: Theme): CSSProperties {
	return {
		[HOVER]: {
			backgroundColor: grey[200],
		},
	};
}

/**
 * Styling for simple clickable item
 */
export function clickableStyle({ palette }: Theme): CSSProperties {
	return {
		[HOVER]: {
			backgroundColor: grey[200],
		},
		[ACTIVE]: {
			backgroundColor: palette.HighlightSelected,
		},
	};
}

/**
 * Styling for Search results and Menu Items
 */
export function itemListStyle({ palette }: Theme): CSSProperties {
	return {
		color: black[100],

		[HOVER_NOT_SELECTED]: {
			backgroundColor: grey[200],
		},
		[FOCUS_VISIBLE]: {
			backgroundColor: grey[200],
		},
		[ACTIVE_NOT_SELECTED]: {
			backgroundColor: palette.NavListItemSelectedBG,
		},
		[SELECTED]: {
			backgroundColor: blue[50],

			[HOVER]: {
				backgroundColor: grey[200],
			},
		},
	};
}

/**
 * Styling for Link Button
 */
export function linkButtonStyle({ palette }: Theme): CSSProperties {
	const { LinkDefault, LinkHover } = palette;

	return {
		borderColor: "transparent",
		color: LinkDefault,
		padding: "4px",
		...childIconCurrentColor,

		[HOVER]: {
			color: LinkHover,
		},
		[FOCUS_VISIBLE]: {
			boxShadow: primaryBoxShadow(palette),
		},
		[ACTIVE]: {
			color: LinkDefault,
		},
		[DISABLED]: {
			color: grey[700],
		},
	};
}

/**
 * Styling for Subtle Link Button
 */
export function linkSubtleButtonStyle({ palette }: Theme): CSSProperties {
	const { grey } = palette;

	return {
		borderColor: "transparent",
		color: grey["A100"],
		padding: "4px",
		...childIconCurrentColor,

		[HOVER]: {
			color: black[100],
			textDecoration: "underline",
		},
		[ACTIVE]: {
			color: grey["A100"],
		},
		[DISABLED]: {
			color: grey[700],
		},
	};
}

/**
 * Styling for DataSetupItemList, RightPane icons
 */
export function navListStyle({ palette }: Theme): CSSProperties {
	const { NavListItemSelectedBG, PrimaryDefault } = palette;

	return {
		borderRightStyle: "solid",
		borderWidth: 3,
		color: black[100],

		[HOVER_NOT_SELECTED]: {
			backgroundColor: grey[200],
			borderColor: grey[500],
		},
		[FOCUS]: {
			backgroundColor: grey[200],
			outline: "none",
		},
		[ACTIVE_NOT_SELECTED]: {
			backgroundColor: NavListItemSelectedBG,
			borderColor: PrimaryDefault,
		},
		[SELECTED]: {
			backgroundColor: NavListItemSelectedBG,
			borderColor: PrimaryDefault,
			color: PrimaryDefault,

			"& .MuiTypography-root": {
				fontWeight: "bold",
			},

			[HOVER]: {
				backgroundColor: grey[200],
			},
		},
	};
}

/**
 * Styling for buttons that blend in with background
 */
export function blendButtonStyle({ palette }: Theme): CSSProperties {
	return {
		borderColor: "transparent",
		color: "inherit",
		...childIconCurrentColor,

		[HOVER]: {
			backgroundColor: "inherit",
		},
		[FOCUS_VISIBLE]: {
			boxShadow: primaryBoxShadow(palette),
		},
		[DISABLED]: {
			color: "inherit",
			opacity: 0.5,
		},
	};
}

/**
 * Styling for default buttons
 */
function _defaultButtonStyle({ palette }: Theme, hoverColor: string, activeColor?: string): CSSProperties {
	return {
		borderColor: "transparent",

		[HOVER]: {
			backgroundColor: hoverColor,
		},
		[FOCUS_VISIBLE]: {
			boxShadow: primaryBoxShadow(palette),
		},
		[ACTIVE]: {
			backgroundColor: activeColor ?? grey[400],
			color: black[400],
		},
		[DISABLED]: {
			color: grey[700],
			...childIconCurrentColor,
		},
	};
}

export function defaultButtonStyle(theme: Theme): CSSProperties {
	return _defaultButtonStyle(theme, grey[200]);
}

export function defaultOnGreyButtonStyle(theme: Theme): CSSProperties {
	return _defaultButtonStyle(theme, grey[400]);
}

export function defaultOnColorButtonStyle(theme: Theme): CSSProperties {
	return _defaultButtonStyle(theme, "none", "none");
}

/**
 * Styling for drag handles
 */
export function dragHandleButtonStyle(): CSSProperties {
	return {
		borderColor: "transparent",
		paddingLeft: 0,
		paddingRight: "4px",

		[DISABLED]: {
			color: grey[700],
			...childIconCurrentColor,
		},
		[HOVER]: {
			cursor: "move",
		},
	};
}

/**
 * Styling for dark mode buttons and icon buttons
 */

export function darkModeButtonStyle(theme: Theme): CSSProperties {
	return {
		borderColor: "transparent",
		color: grey[400],
		[HOVER]: {
			backgroundColor: "transparent",
			color: white[50],
		},
		[ACTIVE]: {
			backgroundColor: "transparent",
			color: white[50],
		},
		[FOCUS_VISIBLE]: {
			boxShadow: primaryBoxShadow(theme.palette),
			backgroundColor: black[100],
		},
		[DISABLED]: {
			color: grey.A100,
			"& svg": {
				color: grey.A100,
			},
		},
		"&[aria-expanded=true]": {
			backgroundColor: white[50],
			color: black[100],
			border: `1px solid ${theme.palette.FocusSecondary}`,
			"& svg": {
				color: grey.A100,
			},
		},
		"& svg": {
			color: grey[400],
			[HOVER]: {
				color: white[50],
			},
			[ACTIVE]: {
				color: white[50],
			},
		},
	};
}

/**
 * Styling for dark mode primary button
 */

export function darkModePrimaryButtonStyle(theme: Theme): CSSProperties {
	return primaryStyleImpl(theme, grey.A100);
}

/**
 * Styling for inactive icon buttons
 */
export function inactiveButtonStyle(theme: Theme): CSSProperties {
	return {
		borderColor: "transparent",
		cursor: "initial",
		pointerEvents: "none",
	};
}

/**
 * Styling for outlined buttons
 */
export function outlineButtonStyle({ palette }: Theme): CSSProperties {
	const boxShadow = primaryBoxShadow(palette);

	return {
		backgroundColor: palette.background.paper,
		borderColor: grey[400],

		[HOVER]: {
			backgroundColor: grey[100],
		},
		[FOCUS_VISIBLE]: {
			boxShadow,
		},
		[ACTIVE]: {
			borderColor: palette.PrimaryDefault,
			boxShadow: boxShadows.activeButton,
		},
		[DISABLED]: {
			backgroundColor: grey[100],
			color: grey[700],
			...childIconCurrentColor,
		},
	};
}

/**
 * Styling for popper paper
 */
export function popperPaperStyle({ palette }: Theme): CSSProperties {
	return {
		borderColor: grey[400],
		borderStyle: "solid",
		borderWidth: 1,
		boxShadow: boxShadows.popper,
		paddingTop: "4px",
		paddingBottom: "4px",
	};
}

/**
 * Styling for primary icon buttons
 */
export function primaryIconStyle({ palette }: Theme): CSSProperties {
	// TODO: Try to consolidate with other icon buttons where only difference is colors
	const { PrimaryDefault, PrimaryDisabledBG, PrimaryHoverPressedBG } = palette;

	return {
		borderColor: "transparent",
		color: PrimaryDefault,

		[HOVER]: {
			backgroundColor: grey[200],
			borderColor: grey[200],
		},
		[FOCUS_VISIBLE]: {
			boxShadow: primaryBoxShadow(palette),
		},
		[ACTIVE]: {
			backgroundColor: grey[400],
			borderColor: grey[400],
			color: PrimaryHoverPressedBG,
		},
		[DISABLED]: {
			color: PrimaryDisabledBG,
		},

		...childIconCurrentColor,
	};
}

/**
 * Styling for primary buttons 
 */
export function primaryStyle(theme: Theme): CSSProperties {
	return primaryStyleImpl(theme, theme.palette.PrimaryDisabledBG);
}

/**
 * Implement styles for primary buttons
 */
function primaryStyleImpl({ palette }: Theme, disabledColor: string): CSSProperties {
	const { PrimaryDefault, PrimaryHoverPressedBG, PrimaryPressedBorder } = palette;

	return {
		backgroundColor: PrimaryDefault,
		borderColor: PrimaryDefault,
		color: white[50],

		[HOVER]: {
			backgroundColor: PrimaryHoverPressedBG,
			borderColor: PrimaryHoverPressedBG,
		},
		[FOCUS_VISIBLE]: {
			boxShadow: primaryBoxShadow(palette),
		},
		[ACTIVE]: {
			backgroundColor: PrimaryHoverPressedBG,
			borderColor: PrimaryPressedBorder,
			boxShadow: boxShadows.activeButton,
		},
		[DISABLED]: {
			backgroundColor: disabledColor,
			borderColor: disabledColor,
			color: white[50],
			...childIconCurrentColor,
		},

		"& .MuiIcon-root": {
			color: white[50],
		},
		"& .MuiSvgIcon-root": {
			color: white[50],
		},
	};
}

/**
 * Styling for secondary buttons in active state
 */
export function secondaryActiveStyle({ palette }: Theme, omitBackground = false): CSSProperties {
	return {
		backgroundColor: omitBackground ? palette.background.paper : grey[400],
		borderColor: palette.PrimaryDefault,

		// made important to override focused boxShadow below when applied when menu is open, but not active
		boxShadow: `${boxShadows.activeButton} !important`,

		[SELECT]: {
			borderColor: palette.PrimaryDefault,
		},
	};
}

/**
 * Styling for secondary buttons; in case of Select, there are cases for applying the background color and not
 */
export function secondaryStyle(theme: Theme, omitBackground = false): CSSProperties {
	const { palette } = theme;
	const background = omitBackground ? palette.background.paper : null;

	return {
		backgroundColor: background || grey[200],
		borderColor: grey[400],

		// secondary style is used with the SelectMenu where the border is applied
		// on the select element that is a child of the root element, so border color needs
		// to be applied in both places
		[SELECT]: {
			borderColor: grey[400],
		},

		[HOVER]: {
			backgroundColor: background || grey[400],
			borderColor: grey[700],

			[SELECT]: {
				borderColor: grey[700],
			},
		},
		[FOCUS_VISIBLE]: {
			boxShadow: primaryBoxShadow(palette),
		},
		[ACTIVE]: secondaryActiveStyle(theme, omitBackground),
		[DISABLED]: {
			backgroundColor: grey[200],
			borderColor: grey[400],
			color: grey[800],
			...childIconCurrentColor,
		},
	};
}

/**
 * Styling for select component
 */
export function selectStyle(theme: Theme): CSSProperties {
	return {
		borderWidth: 0, //the border will be on the TextField
		padding: "4px",

		[DISABLED]: {
			backgroundColor: grey[100],
		},
	};
}

/**
 * Styling for textfields
 */
export function textfieldStyle({ palette }: Theme, bBlackPlaceholder?: boolean): CSSProperties {
	return {
		backgroundColor: white[50],
		borderColor: grey[400],
		color: black[100],

		[HOVER]: {
			borderColor: grey[700],
		},
		[FOCUSED]: {
			borderColor: palette.PrimaryDefault,
		},
		[DISABLED]: {
			backgroundColor: grey[100],
		},
		"& input": {
			[PLACEHOLDER]: bBlackPlaceholder
				? {
					color: black[100],
				}
				: {},
		},
	};
}

/** styling for disabled text */
export function disabledTextStyle({ palette }: Theme): CSSProperties {
	return {
		color: grey[800],
	};
}


/** Returns cursor styling based on whether component should be disabled */
export function disabledCursorStyling(disabled?: boolean): string {
	return disabled ? "not-allowed" : "pointer";
}

/** Adds sx to props safely */
export function propsWithSx(props: ComponentProps<any>, sx: SxProps = {}): ComponentProps<any> {
	if (props) {
		return {
			...props,
			sx: mergeSx(props.sx, sx),
		};
	} else {
		return {
			sx,
		};
	}
}

/**  Merges two sets of SxProps, prioritizing the ones passed to the component and adding the component's own Sx */
export function mergeSx(propSx: SxProps = {}, componentSx: SxProps = {}): SxProps {
	return {
		...componentSx,
		...propSx,
	};
}